import { useNavigate } from 'react-router-dom'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faMagic, faCheck, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import { PolicyStatusBadge } from 'shared/badges'
import { useDeletePolicyDocument, useDownloadPolicyDocument } from 'api/hooks/use-policy-documents'

const PolicyList = ({ policies, onExtract }) => {
  const navigate = useNavigate()
  const { mutate: deletePolicyDocument } = useDeletePolicyDocument()
  const { mutate: downloadDocument } = useDownloadPolicyDocument()

  const handleDelete = (e, id) => {
    e.stopPropagation()
    if (window.confirm('Are you sure you want to delete this policy document?')) {
      deletePolicyDocument(id)
    }
  }

  if (!policies || policies.length === 0) {
    return <p className="text-muted small">No policy documents uploaded yet.</p>
  }

  return (
    <Table hover size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Sections</th>
          <th>Status</th>
          <th>Matching</th>
          <th>Rules</th>
          <th>Pages</th>
          <th>Uploaded By</th>
          <th>Date</th>
          <th className="text-end">Actions</th>
        </tr>
      </thead>
      <tbody>
        {policies.map((doc) => (
          <tr key={doc.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/policies/${doc.id}`)}>
            <td>
              {doc.name}
              {doc.has_document && (
                <a
                  href={doc.document_url}
                  className="ms-2 text-muted"
                  title={`Download ${doc.file_name || 'document'}`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    downloadDocument({ documentUrl: doc.document_url, fileName: doc.file_name })
                  }}
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                </a>
              )}
            </td>
            <td>
              {doc.sections?.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {doc.sections.map((s) => (
                    <Badge key={s.id} className="badge-secondary" bg="" style={{ fontWeight: 'normal' }}>{s.name}</Badge>
                  ))}
                </div>
              ) : (
                <small className="text-muted">-</small>
              )}
            </td>
            <td>
              {doc.has_document ? (
                <>
                  <PolicyStatusBadge status={doc.status} />
                  {doc.error_message && (
                    <small className="text-danger ms-2" title={doc.error_message}>
                      {doc.error_message.length > 50 ? `${doc.error_message.substring(0, 50)}...` : doc.error_message}
                    </small>
                  )}
                </>
              ) : (
                <small className="text-muted">-</small>
              )}
            </td>
            <td>
              {doc.matching_status === 'matching' && (
                <small className="text-muted fst-italic">In Progress</small>
              )}
              {doc.matching_status === 'matched' && (
                <small><FontAwesomeIcon icon={faCheck} className="text-success me-1" />Matched</small>
              )}
              {doc.matching_status === 'match_failed' && (
                <small className="text-danger">Match failed</small>
              )}
              {!doc.matching_status && (
                <small className="text-muted">-</small>
              )}
            </td>
            <td>{doc.rules_count || '-'}</td>
            <td>{doc.page_count || '-'}</td>
            <td>{doc.uploaded_by?.full_name}</td>
            <td>{new Date(doc.created_at).toLocaleDateString()}</td>
            <td className="text-end" onClick={(e) => e.stopPropagation()}>
              {doc.has_document && doc.status === 'failed' && (
                <Button variant="outline-primary" size="sm" className="me-1" onClick={() => onExtract(doc.id)}>
                  <FontAwesomeIcon icon={faMagic} className="me-1" />
                  Retry Extraction
                </Button>
              )}
              {doc.has_document && doc.status === 'extracting' && (
                <span className="text-muted fst-italic small me-1">
                  <Spinner as="span" animation="border" size="sm" className="me-1" />
                  Extracting...
                </span>
              )}
              <Button variant="outline-danger" size="sm" onClick={(e) => handleDelete(e, doc.id)}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

export default PolicyList
